import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import {
  Guide,
  GuideListResponse,
  GuideFilters,
  GuideFormData,
  GuideCompletion,
  CompletionFormData,
  AgencyProgress
} from '../types/EducationHub';


// Custom hook for guide management
export const useGuides = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getAdminHeaders = () => {
    const token = sessionStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchGuides = useCallback(async (filters: GuideFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.GUIDES, {
        params: filters,
        headers: getAuthHeaders()
      });

      const data: GuideListResponse = response.data;
      setGuides(data.guides || []);
      setTotalCount(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (err: any) {
      // Always ensure guides is an array, never undefined
      setGuides([]);
      setTotalCount(0);
      setHasMore(false);
      
      // Only log errors that are not authentication related
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch guides');
        console.error('Error fetching guides:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Super-admin operations ──────────────────────────────────────────────────

  const fetchAdminGuides = useCallback(async (): Promise<Guide[]> => {
    const { data } = await axios.get(EDUCATION_ENDPOINTS.GUIDES_ADMIN, { headers: getAdminHeaders() });
    return data.guides || [];
  }, []);

  const createGuide = useCallback(async (guideData: GuideFormData): Promise<Guide | null> => {
    setError(null);
    try {
      const { data } = await axios.post(EDUCATION_ENDPOINTS.GUIDES, guideData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create guide');
      throw err;
    }
  }, []);

  const updateGuide = useCallback(async (id: string, guideData: Partial<GuideFormData>): Promise<Guide | null> => {
    setError(null);
    try {
      const { data } = await axios.put(EDUCATION_ENDPOINTS.GUIDES_ITEM(id), guideData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update guide');
      throw err;
    }
  }, []);

  const deleteGuide = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await axios.delete(EDUCATION_ENDPOINTS.GUIDES_ITEM(id), { headers: getAdminHeaders() });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete guide');
      throw err;
    }
  }, []);

  const getGuideById = useCallback(async (id: string): Promise<Guide | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.GUIDES_CONTENT(id), {
        headers: getAuthHeaders()
      });

      
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch guide');
      console.error('Error fetching guide:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    guides,
    loading,
    error,
    totalCount,
    hasMore,
    fetchGuides,
    fetchAdminGuides,
    createGuide,
    updateGuide,
    deleteGuide,
    getGuideById
  };
};

// Custom hook for guide completions
export const useGuideCompletions = () => {
  const [completions, setCompletions] = useState<GuideCompletion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getUserCompletions = useCallback(async (userId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = userId 
        ? `${EDUCATION_ENDPOINTS.GUIDE_COMPLETIONS}/user/${userId}`
        : `${EDUCATION_ENDPOINTS.GUIDE_COMPLETIONS}/user`;
        
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      
      setCompletions(response.data?.completions || []);
      return response.data?.completions || [];
    } catch (err: any) {
      // Always ensure completions is an array, never undefined
      setCompletions([]);
      
      // Only log errors that are not authentication related
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch completions');
        console.error('Error fetching completions:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getGuideCompletion = useCallback(async (guideId: string, userId?: string): Promise<GuideCompletion | null> => {
    setError(null);
    
    try {
      const url = userId 
        ? `${EDUCATION_ENDPOINTS.GUIDE_COMPLETIONS}/guide/${guideId}/user/${userId}`
        : `${EDUCATION_ENDPOINTS.GUIDE_COMPLETIONS}/guide/${guideId}/user`;
        
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to fetch completion');
        console.error('Error fetching completion:', err);
      }
      return null;
    }
  }, []);

  const updateCompletion = useCallback(async (guideId: string, completionData: CompletionFormData): Promise<GuideCompletion | null> => {
    setLoading(true);
    setError(null);
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${EDUCATION_ENDPOINTS.GUIDE_COMPLETIONS}/guide/${guideId}`, completionData, {
        headers: getAuthHeaders()
      });
      const updatedCompletion: GuideCompletion = response.data;
      setCompletions(prev => {
        const existing = prev.find(c => c.guideId && c.guideId._id === guideId);
        if (existing) {
          return prev.map(c => (c.guideId && c.guideId._id === guideId) ? updatedCompletion : c);
        } else {
          return [...prev, updatedCompletion];
        }
      });
      return updatedCompletion;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update completion');
      console.error('Error updating completion:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    completions,
    loading,
    error,
    getUserCompletions,
    getGuideCompletion,
    updateCompletion
  };
};

// Custom hook for guide metadata
export const useGuideMetadata = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCategories = useCallback(async () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      setCategories([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.GUIDES_CATEGORIES, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    refetchCategories: fetchCategories,
  };
};
